/**
 * Host dock — the SIDEBAR (right-edge rail) layout pushes the host page
 * aside instead of covering it.
 *
 * David 2026-10-20: "the rail goes over my website, we want it docked on
 * the side, right … it does not cover the web page, this is much better."
 * v3 had deliberately left the rail as an overlay ("v3 doesn't mutate the
 * host body — sites with their own padding rules would break"). The dock
 * avoids body padding: it narrows the ROOT element by the rail's width so
 * normal-flow content reflows into the remaining space. (A transform on
 * the root was tried and rejected: it re-anchors every position:fixed
 * element — site header, cookie bar, the rail itself — to the document
 * instead of the viewport, so they scroll away and the rail grows to the
 * document's height.)
 *
 * position:fixed site chrome stays viewport-relative and would run under
 * the rail, so full-width fixed elements (left:0 … right:0 headers, bottom
 * bars) get `right: <rail width>` pinned inline while docked and restored
 * on undock. Host `100vw` units still mean the full viewport; sections
 * sized that way run under the rail. Accepted.
 *
 * Off on phones (the rail is full-screen there anyway) and when the tag
 * carries `data-showpilot-rail="overlay"`.
 */

export const HOST_DOCK_STYLE_ID = 'showpilot-host-dock';
export const HOST_DOCK_CLASS = 'sp-host-docked';
const WIDTH_VAR = '--sp-host-dock-width';
/** Fixed elements at least this wide (share of the viewport) count as chrome to pin. */
const FULL_WIDTH_SHARE = 0.9;

export type RailMode = 'dock' | 'overlay';

/** Tag attribute → mode. Anything but an explicit "overlay" docks. */
export function railModeFromAttr(value: string | null | undefined): RailMode {
  return value?.trim().toLowerCase() === 'overlay' ? 'overlay' : 'dock';
}

/** Dock only where the rail is a side panel, never where it takes the screen. */
export function shouldDock(opts: {
  mode: RailMode;
  layout: string;
  device: 'phone' | 'tablet' | 'desktop';
}): boolean {
  return opts.mode === 'dock' && opts.layout === 'sidebar' && opts.device !== 'phone';
}

/** A fixed element that spans (nearly) the whole viewport width is site chrome. */
export function isFullWidthFixed(
  position: string,
  rect: { left: number; width: number },
  viewportWidth: number,
): boolean {
  return (
    position === 'fixed' &&
    viewportWidth > 0 &&
    rect.left <= 1 &&
    rect.width >= viewportWidth * FULL_WIDTH_SHARE
  );
}

function ensureStyle(doc: Document): void {
  if (doc.getElementById(HOST_DOCK_STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = HOST_DOCK_STYLE_ID;
  style.textContent = [
    `html.${HOST_DOCK_CLASS}{`,
    `width:calc(100% - var(${WIDTH_VAR},0px)) !important;`,
    'transition:width .28s ease;',
    '}',
    `@media (prefers-reduced-motion: reduce){html.${HOST_DOCK_CLASS}{transition:none;}}`,
  ].join('');
  doc.head.appendChild(style);
}

/** Inline `right` values we overrode, so undock can put them back exactly. */
const pinned = new WeakMap<HTMLElement, string>();
let pinnedList: HTMLElement[] = [];

function pinFixedChrome(doc: Document, widthPx: number, isWidget: (el: Element) => boolean): void {
  const view = doc.defaultView;
  if (!view) return;
  const vw = view.innerWidth;
  const next: HTMLElement[] = [];
  const all = doc.body ? doc.body.querySelectorAll<HTMLElement>('*') : [];
  for (const el of all) {
    if (isWidget(el)) continue;
    const cs = view.getComputedStyle(el);
    if (cs.position !== 'fixed') continue;
    const rect = el.getBoundingClientRect();
    const already = pinned.has(el);
    if (!already && !isFullWidthFixed(cs.position, rect, vw)) continue;
    if (!already) pinned.set(el, el.style.right);
    el.style.right = `${Math.round(widthPx)}px`;
    next.push(el);
  }
  pinnedList = next;
}

function unpinFixedChrome(): void {
  for (const el of pinnedList) {
    const original = pinned.get(el);
    if (original === undefined) continue;
    el.style.right = original;
    pinned.delete(el);
  }
  pinnedList = [];
}

/** Push the host page aside by `widthPx`. Idempotent; re-call on resize. */
export function applyHostDock(
  doc: Document,
  widthPx: number,
  isWidget: (el: Element) => boolean = (el) => el.closest('.sp-root') !== null,
): void {
  const root = doc.documentElement;
  ensureStyle(doc);
  root.style.setProperty(WIDTH_VAR, `${Math.max(0, Math.round(widthPx))}px`);
  root.classList.add(HOST_DOCK_CLASS);
  pinFixedChrome(doc, widthPx, isWidget);
}

/** Give the page its full width back. Safe when nothing was applied. */
export function removeHostDock(doc: Document): void {
  const root = doc.documentElement;
  root.classList.remove(HOST_DOCK_CLASS);
  root.style.removeProperty(WIDTH_VAR);
  unpinFixedChrome();
}

export function isHostDocked(doc: Document): boolean {
  return doc.documentElement.classList.contains(HOST_DOCK_CLASS);
}
